import type { RouteLocation } from 'vue-router';
import { type ComponentRoute } from './types';

const allMainRoutes: ComponentRoute[] = [
  {
    name: 'Analytics',
    path: '/analytics',
    component: () => import('@/views/analytics/AnalyticsPage.vue'),
    meta: {
      permission: {
        action: 'manage',
        subject: 'Analytics',
      },
    },
  },
  {
    path: '/mental-state-chats/:chatId?',
    name: 'MentalStateChats',
    component: () => import('@/views/chats/mental-state-chats/MentalStateChatView.vue'),
    meta: {
      permission: {
        action: 'manage',
        subject: 'MentalStateChats',
      },
    },
    props: true,
  },
  {
    path: '/psychology-chats/:chatId?',
    name: 'PsychoChats',
    component: () => import('@/views/chats/psychology-chats/ChatView.vue'),
    meta: {
      permission: {
        action: 'manage',
        subject: 'PsychologyChats',
      },
    },
    props: true,
  },
  {
    name: 'Companies',
    path: '/companies',
    component: () => import('@/views/companies/CompaniesList.vue'),
    meta: {
      permission: {
        action: 'manage',
        subject: 'Companies',
      },
    },
  },
  {
    name: 'CompanyTeams',
    path: '/companies/:companyId',
    component: () => import('@/views/companies/components/CompanyTeamsList.vue'),
    meta: {
      permission: {
        action: 'manage',
        subject: 'CompanyTeams',
      },
    },
  },
  {
    name: 'Consultant Requests',
    path: '/consultant-requests',
    component: () => import('@/views/consultantRequests/ConsultantRequestsList.vue'),
    meta: {
      permission: {
        action: 'manage',
        subject: 'ConsultantRequests',
      },
    },
  },
  {
    name: 'Remote Config',
    path: '/remote-config',
    component: () => import('@/views/remoteConfig/remoteConfigSettings.vue'),
    meta: {
      permission: {
        action: 'manage',
        subject: 'RemoteConfig',
      },
    },
  },
  {
    name: 'Journeys',
    path: '/journeys',
    component: () => import('@/views/journeys/pages/JourneysList.vue'),
    meta: {
      permission: {
        action: 'manage',
        subject: 'Journey',
      },
    },
  },
  {
    name: 'JourneyDetails',
    path: '/journeys/:journeyId/tasks',
    component: () => import('@/views/journeys/pages/JourneyDetails.vue'),
    meta: {
      permission: {
        action: 'manage',
        subject: 'Journey',
      },
    },
  },
  {
    name: 'JourneyTaskEdit',
    path: '/journeys/:journeyId/tasks/:taskId',
    component: () => import('@/views/journeys/pages/JourneyTaskEditPage.vue'),
    meta: {
      permission: {
        action: 'manage',
        subject: 'Journey',
      },
    },
  },
  {
    name: 'RequestDetails',
    path: '/consultant-requests/:id',
    component: () => import('@/views/consultantRequests/components/ConsultantRequest.vue'),
    meta: {
      permission: {
        action: 'manage',
        subject: 'ConsultantRequest',
      },
    },
  },
  {
    name: 'Departments',
    path: '/departments',
    component: () => import('@/views/departments/DepartmentsList.vue'),
    meta: {
      permission: {
        action: 'manage',
        subject: 'Departments',
      },
    },
  },
  {
    name: 'DepartmentDetails',
    path: '/departments/:departmentId',
    redirect: (to: RouteLocation) => {
      const departmentId = to.params.departmentId;
      return `/departments/${departmentId}/teams`;
    },
    props: true,
    meta: {
      permission: {
        action: 'manage',
        subject: 'Department',
      },
    },
    children: [
      {
        path: 'teams',
        name: 'department-teams',
        component: () => import('@/views/departments/DepartmentDetails.vue'),
        props: true,
        meta: {
          permission: {
            action: 'manage',
            subject: 'Department',
          },
        },
      },
      {
        path: 'analytics',
        name: 'department-analytics',
        component: () => import('@/views/departments/DepartmentDetails.vue'),
        props: true,
        meta: {
          permission: {
            action: 'manage',
            subject: 'Department',
          },
        },
      },
    ],
  },
  {
    name: 'Company-Insights',
    path: '/company-insights',
    redirect: '/insights/new',
    meta: {
      permission: {
        action: 'manage',
        subject: 'CompanyInsights',
      },
    },
    children: [
      {
        path: '/insights/new',
        name: 'new-insights',
        component: () => import('@/views/companyInsights/CompanyInsights.vue'),
        meta: {
          permission: {
            action: 'manage',
            subject: 'CompanyInsights',
          },
        },
      },
      {
        path: '/insights/ordered',
        name: 'ordered-insights',
        component: () => import('@/views/companyInsights/CompanyInsights.vue'),
        meta: {
          permission: {
            action: 'manage',
            subject: 'CompanyInsights',
          },
        },
      },
      {
        path: '/insights/later',
        name: 'later-insights',
        component: () => import('@/views/companyInsights/CompanyInsights.vue'),
        meta: {
          permission: {
            action: 'manage',
            subject: 'CompanyInsights',
          },
        },
      },
    ],
  },
  {
    name: 'Insights',
    path: '/insights',
    redirect: '/insights/active',
    meta: {
      permission: {
        action: 'manage',
        subject: 'Insights',
      },
    },
    children: [
      {
        path: '/insights/active',
        name: 'active-insights',
        component: () => import('@/views/insights/Insights.vue'),
        meta: {
          permission: {
            action: 'manage',
            subject: 'Insights',
          },
        },
      },
      {
        path: '/insights/unactive',
        name: 'unactive-insights',
        component: () => import('@/views/insights/Insights.vue'),
        meta: {
          permission: {
            action: 'manage',
            subject: 'Insights',
          },
        },
      },
    ],
  },
  {
    name: 'InsightEditPage',
    path: '/insights/edit/:insightId',
    component: () => import('@/views/insights/components/InsightEditPage.vue'),
    meta: {
      permission: {
        action: 'edit',
        subject: 'Insights',
      },
    },
  },
  {
    name: 'InsightCreatePage',
    path: '/insights/create',
    component: () => import('@/views/insights/components/InsightCreatePage.vue'),
    meta: {
      permission: {
        action: 'create',
        subject: 'Insights',
      },
    },
  },
  {
    name: 'CompanyInsightData',
    path: '/insights/:insightId',
    component: () => import('@/views/companyInsights/components/CompanyInsightData.vue'),
    meta: {
      permission: {
        action: 'manage',
        subject: 'CompanyInsights',
      },
    },
  },
  {
    name: 'Feedbacks',
    path: '/feedbacks',
    component: () => import('@/views/feedbacks/FeedbacksList.vue'),
    meta: {
      permission: {
        action: 'manage',
        subject: 'Feedbacks',
      },
    },
  },
  {
    name: 'FeedbackDetails',
    path: '/feedbacks/:feedbackId',
    component: () => import('@/views/feedbacks/components/FeedbackDetails.vue'),
    meta: {
      permission: {
        action: 'manage',
        subject: 'Feedbacks',
      },
    },
  },
  {
    name: 'Teams',
    path: '/teams',
    component: () => import('@/views/teams/TeamsList.vue'),
    meta: {
      permission: {
        action: 'manage',
        subject: 'Teams',
      },
    },
  },
  {
    name: 'TeamDetails',
    path: '/teams/:teamId',
    redirect: (to: RouteLocation) => {
      const teamId = to.params.teamId;
      return `/teams/${teamId}/users`;
    },
    meta: {
      permission: {
        action: 'manage',
        subject: 'Team',
      },
    },
    children: [
      {
        path: 'users',
        name: 'team-users',
        component: () => import('@/views/teams/TeamDetails.vue'),
        props: true,
        meta: {
          permission: {
            action: 'manage',
            subject: 'Team',
          },
        },
      },
      {
        path: 'analytics',
        name: 'team-analytics',
        component: () => import('@/views/teams/TeamDetails.vue'),
        props: true,
        meta: {
          permission: {
            action: 'manage',
            subject: 'Team',
          },
        },
      },
      {
        path: 'insights',
        name: 'team-insights',
        component: () => import('@/views/teams/TeamDetails.vue'),
        props: true,
        meta: {
          permission: {
            action: 'manage',
            subject: 'CompanyInsights',
          },
        },
      },
    ],
  },
  {
    name: 'Users List',
    path: '/users',
    component: () => import('@/views/users/UsersList.vue'),
    meta: {
      permission: {
        action: 'manage',
        subject: 'Users',
      },
    },
  },
  {
    name: 'UserEditPage',
    path: '/users/:userId',
    component: () => import('@/views/users/components/UserEditPage.vue'),
    meta: {
      permission: {
        action: 'manage',
        subject: 'User',
      },
    },
  },
  {
    name: 'User Profile',
    path: '/profile/edit',
    component: () => import('@/views/profile/UserProfile.vue'),
    meta: {
      permission: {
        action: 'manage',
        subject: 'Profile',
      },
    },
  },
  {
    name: 'Pricing',
    path: '/pricing',
    component: () => import('@/views/pricing/PricingList.vue'),
    meta: {
      permission: {
        action: 'manage',
        subject: 'Pricing',
      },
    },
  },
  {
    name: 'Articles',
    path: '/articles',
    component: () => import('@/views/articles/ArticlesList.vue'),
    meta: {
      permission: {
        action: 'read',
        subject: 'Articles',
      },
    },
  },
  {
    path: '/articles/create',
    name: 'ArticleCreate',
    component: () => import('@/views/articles/components/ArticleEditor.vue'),
  },
  {
    path: '/articles/:id/edit',
    name: 'ArticleEdit',
    component: () => import('@/views/articles/components/ArticleEditor.vue'),
  },
  {
    name: 'Achievements',
    path: '/achievements',
    component: () => import('@/views/achievements/AchievementsList.vue'),
    meta: {
      permission: {
        action: 'manage',
        subject: 'Achievements',
      },
    },
  },
  {
    path: '/achievements/:id/edit',
    name: 'AchievementEdit',
    component: () => import('@/views/achievements/components/AchievementEdit.vue'),
    meta: {
      permission: {
        action: 'manage',
        subject: 'Achievements',
      },
    },
  },
  {
    name: 'Sessions',
    component: () => import('@/views/sessions/SessionsLayout.vue'),
    path: '/sessions',
    redirect: '/sessions/board',
    meta: {
      permission: {
        action: 'manage',
        subject: 'Sessions',
      },
    },
    children: [
      {
        path: 'board',
        name: 'sessions-board',
        component: () => import('@/views/sessions/SessionsKanban.vue'),
        meta: {
          permission: {
            action: 'manage',
            subject: 'Sessions',
          },
        },
      },
      {
        path: 'list',
        name: 'sessions-list',
        component: () => import('@/views/sessions/SessionsList.vue'),
        meta: {
          permission: {
            action: 'manage',
            subject: 'Sessions',
          },
        },
      },
      {
        path: 'calendar',
        name: 'sessions-calendar',
        component: () => import('@/views/sessions/SessionsCalendar.vue'),
        meta: {
          permission: {
            action: 'manage',
            subject: 'Sessions',
          },
        },
      },
    ],
  },
  {
    name: 'Psychologist Patients',
    path: '/patients',
    component: () => import('@/views/psychologist/patients/PsychoPatientsList.vue'),
    meta: {
      permission: {
        action: 'manage',
        subject: 'PsychoPatients',
      },
    },
  },
  {
    name: 'PatientDetails',
    path: '/patients/:id',
    component: () => import('@/views/psychologist/patients/components/PsychoPatientData.vue'),
    meta: {
      permission: {
        action: 'manage',
        subject: 'PsychoPatients',
      },
    },
  },
  {
    name: 'Psychologists',
    path: '/psychologists',
    component: () => import('@/views/psychoCompanyManager/psychologists/PsychologistsList.vue'),
    meta: {
      permission: {
        action: 'manage',
        subject: 'Psychologists',
      },
    },
  },
  {
    name: 'PsychologistDetails',
    path: '/psychologists/:id',
    component: () => import('@/views/psychoCompanyManager/psychologists/components/PsychologistDetailsLayout.vue'),
    redirect: (to: RouteLocation) => {
      const psychoId = to.params.id;
      return `/psychologists/${psychoId}/profile`;
    },
    meta: {
      permission: {
        action: 'manage',
        subject: 'Psychologists',
      },
    },
    children: [
      {
        name: 'psychologist-profile',
        path: 'profile',
        component: () => import('@/views/psychoCompanyManager/psychologists/components/PsychologistProfile.vue'),
        meta: {
          permission: {
            action: 'manage',
            subject: 'Psychologists',
          },
        },
      },
      {
        name: 'psychologist-analytics',
        path: 'analytics',
        component: () => import('@/views/psychoCompanyManager/psychologists/components/PsychologistAnalytics.vue'),
        meta: {
          permission: {
            action: 'manage',
            subject: 'Psychologists',
          },
        },
      },
    ],
  },
  {
    name: 'my-psychologist-analytics',
    path: '/my-analytics',
    component: () => import('@/views/psychoCompanyManager/psychologists/components/PsychologistAnalytics.vue'),
    meta: {
      permission: {
        action: 'manage',
        subject: 'PsychologistAnalytics',
      },
    },
  },
  {
    name: 'psycho-company-analytics',
    path: 'company-analytics',
    component: () => import('@/views/psychoCompanyManager/analytics/AnalyticsPage.vue'),
    meta: {
      permission: {
        action: 'manage',
        subject: 'PsychoCompanyAnalytics',
      },
    },
  },
  {
    name: 'psycho-company',
    path: 'settings/company',
    component: () => import('@/views/psychoCompanyManager/CompanyProfile.vue'),
    meta: {
      permission: {
        action: 'manage',
        subject: 'PsychoCompany',
      },
    },
  },
  {
    name: 'integrations',
    path: 'settings/integrations',
    component: () => import('@/views/integrations/IntegrationsPage.vue'),
    meta: {
      permission: {
        action: 'manage',
        subject: 'Integrations',
      },
    },
  },
  {
    name: 'SlackWorkspaceManagement',
    path: 'settings/integrations/slack-workspaces',
    component: () => import('@/views/integrations/SlackWorkspaceManagementPage.vue'),
    meta: {
      permission: {
        action: 'manage',
        subject: 'Integrations',
      },
    },
  },
  {
    name: 'TeamsTenantManagement',
    path: 'settings/integrations/teams-tenants',
    component: () => import('@/views/integrations/TeamsTenantManagementPage.vue'),
    meta: {
      permission: {
        action: 'manage',
        subject: 'Integrations',
      },
    },
  },
  {
    name: 'TeamsTenantLink',
    path: '/integrations/teams/link',
    component: () => import('@/views/integrations/TeamsTenantLinkPage.vue'),
    meta: {
      permission: {
        action: 'manage',
        subject: 'Integrations',
      },
    },
  },
];

const MainRoutes = {
  path: '/',
  meta: {
    requiresAuth: true,
  },
  redirect: '/profile/edit',
  component: () => import('@/layouts/dashboard/DashboardLayout.vue'),
  children: allMainRoutes,
};

export default MainRoutes;
